import React from 'react'
import API from '@aws-amplify/api'
import { debounce } from 'lodash-es'

interface UseAutocompleteProps<T> {
  url: string
  name?: string
  loadAll?: boolean
  showInactive?: boolean
  showDetailedTable?: boolean
  extraParams?: Record<string, any>
  brandId?: string
  responseMapper: (item: T) => string
  getMatchedData?: (data: T[]) => void
  onInputChange?: (value: string, reason: string) => void
  loadSalesOrderDisplayId?: boolean
  loadByProductName?: boolean
  loadBySearch?: boolean
}

interface UseAutocompleteReturn<T> {
  options: string[]
  localData: T[]
  isLoading: boolean
  tempText: string
  setTempText: (text: string) => void
  isUserTyping: boolean
  setIsUserTyping: (isTyping: boolean) => void
  loadAutocomplete: (query: string, brandId?: string) => void
}

export function useAutocomplete<T>(props: UseAutocompleteProps<T>): UseAutocompleteReturn<T> {
  const {
    url,
    name,
    loadAll,
    showInactive,
    showDetailedTable,
    extraParams,
    brandId,
    responseMapper,
    getMatchedData,
    onInputChange,
    loadSalesOrderDisplayId,
    loadByProductName,
    loadBySearch,
  } = props

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [localData, setLocalData] = React.useState<T[]>([])
  const [options, setOptions] = React.useState<string[]>(name ? [name] : [])
  const [tempText, setTempText] = React.useState(name || '')
  const [isUserTyping, setIsUserTyping] = React.useState(false)

  const promiseRef = React.useRef<any>(null)
  const lastLoadedBrandId = React.useRef<string | undefined>(undefined)
  const lastLoadedExtraParams = React.useRef<string | undefined>(undefined)

  // Stable refs for callbacks and config
  const responseMapperRef = React.useRef(responseMapper)
  const getMatchedDataRef = React.useRef(getMatchedData)
  const onInputChangeRef = React.useRef(onInputChange)
  const extraParamsRef = React.useRef(extraParams)
  const optionsRef = React.useRef(options)
  const localDataRef = React.useRef(localData)

  React.useEffect(() => {
    responseMapperRef.current = responseMapper
  }, [responseMapper])

  React.useEffect(() => {
    getMatchedDataRef.current = getMatchedData
  }, [getMatchedData])

  React.useEffect(() => {
    onInputChangeRef.current = onInputChange
  }, [onInputChange])

  React.useEffect(() => {
    extraParamsRef.current = extraParams
  }, [extraParams])

  React.useEffect(() => {
    optionsRef.current = options
  }, [options])

  React.useEffect(() => {
    localDataRef.current = localData
  }, [localData])

  // Sync tempText with name prop when not typing
  React.useEffect(() => {
    if (name !== undefined && !isUserTyping) {
      const matchedOption = options.find((opt) =>
        name ? String(opt).startsWith(String(name)) : false
      )
      const nextText = matchedOption || name || ''
      if (nextText !== tempText) {
        setTempText(nextText)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, options, isUserTyping])

  React.useEffect(() => {
    if (options.length === 0 && name) {
      setOptions([name])
    }
  }, [name, options.length])

  const loadAutocomplete = React.useCallback(
    (query: string, currentBrandId?: string) => {
      // Logic for skipping fetch if query matches loaded options
      const idPart =
        query && String(query).includes(' - ')
          ? String(query).split(' - ')[0]
          : query

      const isSameConfig =
        currentBrandId === lastLoadedBrandId.current &&
        JSON.stringify(extraParamsRef.current) === lastLoadedExtraParams.current

      if (
        query &&
        !loadAll &&
        isSameConfig &&
        optionsRef.current.length > 0 &&
        localDataRef.current.length > 0
      ) {
        const matchesOption =
          optionsRef.current.includes(query) ||
          (idPart &&
            optionsRef.current.some((o) =>
              String(o).startsWith(String(idPart))
            ))
        if (matchesOption) {
          getMatchedDataRef.current?.(localDataRef.current)
          return
        }
      }

      if ((!query || String(query).trim().length === 0) && !loadAll) {
        return
      }

      setIsLoading(true)
      if (promiseRef.current) {
        try {
          API.cancel(promiseRef.current)
        } catch (e) { /* ignore cancel errors */ }
      }

      const param = {
        queryStringParameters: {
          ...(showInactive && { showInactive }),
          ...(currentBrandId && { brandId: currentBrandId }),
          ...((!loadAll || showDetailedTable) && { fuzzy: true }),
          ...(!loadAll && { pageSize: 20 }),
          ...(loadAll && { pageSize: 10000 }),
          ...(loadSalesOrderDisplayId && {
            [(extraParamsRef.current?.factoryType?.toLowerCase() === 'ot'
              ? 'otId'
              : 'soId')]: query,
            isIdSearch: true,
            factoryType: extraParamsRef.current?.factoryType || 'LWS',
          }),
          ...(loadByProductName && query && { productName: query }),
          ...(loadBySearch && query && { search: query }),
          ...extraParamsRef.current,
        },
      }

      let urlToUse = url
      // Construct URL logic
      if (
        (!loadAll || showDetailedTable) &&
        !loadSalesOrderDisplayId &&
        !loadByProductName &&
        !loadBySearch
      ) {
        const idForUrl = idPart || query
        if (idForUrl) {
          const [base, queryString] = url.split('?')
          const separator = base.endsWith('/') ? '' : '/'
          urlToUse =
            base + separator + idForUrl + (queryString ? '?' + queryString : '')
        }
      }

      const requestPromise = API.get('products', urlToUse, param)
      promiseRef.current = requestPromise

      requestPromise
        .then((response: any) => {
          let mappedResponse = response
          // Handle different response structures
          if (loadSalesOrderDisplayId) {
            mappedResponse = response.salesOrders
          } else if (response?.batches && Array.isArray(response.batches)) {
            mappedResponse = response.batches
          } else if (response?.data && Array.isArray(response.data)) {
            mappedResponse = response.data
          }

          if (Array.isArray(mappedResponse) && mappedResponse.length) {
            const newOptions = mappedResponse.map((data: T) => responseMapperRef.current(data))
            setOptions(newOptions)
            setLocalData(mappedResponse)
            getMatchedDataRef.current?.(mappedResponse)
          } else {
            setOptions([])
            setLocalData([])
            getMatchedDataRef.current?.([])
          }
          lastLoadedBrandId.current = currentBrandId
          lastLoadedExtraParams.current = JSON.stringify(extraParamsRef.current)
        })
        .catch((error: any) => {
          if (!API.isCancel(error)) {
            setOptions([])
            setLocalData([])
            getMatchedDataRef.current?.([])
            lastLoadedBrandId.current = currentBrandId
            lastLoadedExtraParams.current = JSON.stringify(extraParamsRef.current)
          }
        })
        .finally(() => {
          // check if this is still the latest request
          if (promiseRef.current === requestPromise) {
            setIsLoading(false)
          }
        })
    },
    [
      loadAll,
      showInactive,
      showDetailedTable,
      loadSalesOrderDisplayId,
      loadByProductName,
      loadBySearch,
      url // url is assumed stable or worth re-fetching if changed
    ]
  )

  const throttledLoadAutocomplete = React.useMemo(
    () => debounce(loadAutocomplete, 250),
    [loadAutocomplete]
  )

  // Stringify extraParams to avoid infinite loops on unstable object references
  // but still trigger reload when the actual content changes.
  const extraParamsString = JSON.stringify(extraParams)

  // 1. Fetch for loadAll: only when brandId, extraParamsString, or loadAll changes.
  React.useEffect(() => {
    if (loadAll) {
      loadAutocomplete('', brandId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, extraParamsString, loadAll])

  // 2. Fetch for server-side search (!loadAll): when tempText, brandId, or extraParamsString changes.
  React.useEffect(() => {
    if (!loadAll) {
      throttledLoadAutocomplete(tempText, brandId)
    }
    return () => {
      throttledLoadAutocomplete.cancel()
    }
  }, [tempText, brandId, throttledLoadAutocomplete, loadAll, extraParamsString])


  return {
    options,
    localData,
    isLoading,
    tempText,
    setTempText,
    isUserTyping,
    setIsUserTyping,
    loadAutocomplete, // exposed for manual triggering if needed
  }
}
